// state/appendice-identity.ts — Appendice restyle (founder-locked "go",
// 2026-07-14): color as WAYFINDING. Every argomento owns a stable identity hue
// from a 5-color cycle inside the brand family; the hue colors that
// argomento's chrome everywhere (index medallion/rail, argomento header,
// In breve tint, sotto-argomento markers, question-card edges, teoria reader
// accents) so the section reads alive AND tells you where you are.
//
// Two hard rules the palette respects:
//   - long theory prose stays on quiet backgrounds (dyslexia) — hues color
//     chrome, never text grounds;
//   - the SEMANTIC triad stays reserved: green = right, rose = wrong,
//     amber = da-rinforzare / traps. Identity hues deliberately avoid all
//     three families.
//
// Contrast (WCAG, verified 2026-07-14): `text` variants are ≥5.1:1 on the
// lavender ground (#f7f4fc) and on white cards; white text on every `grad`
// start is ≥4.6:1 and on every end ≥3.4:1 (headers render ≥16px bold).

export interface ArgomentoHue {
  /** Medallion / rail / edge color (decorative surfaces, not small text). */
  solid: string;
  /** Small-text-safe variant for labels on ground/white (≥5.1:1). */
  text: string;
  /** 12% tint for medallion fills. */
  tint: string;
  /** 8% tint for card washes (In breve). */
  soft: string;
  /** 25% border for tinted cards. */
  border: string;
  /** Header-band gradient (white text safe). */
  grad: string;
}

export const APPENDICE_HUES: readonly ArgomentoHue[] = [
  { // violet
    solid: '#6943ff', text: '#5a35e6',
    tint: 'rgba(105, 67, 255, 0.12)', soft: 'rgba(105, 67, 255, 0.08)',
    border: 'rgba(105, 67, 255, 0.28)',
    grad: 'linear-gradient(120deg, #5a35e6, #8f4bee)',
  },
  { // blue
    solid: '#2f6bff', text: '#2456d9',
    tint: 'rgba(47, 107, 255, 0.12)', soft: 'rgba(47, 107, 255, 0.08)',
    border: 'rgba(47, 107, 255, 0.28)',
    grad: 'linear-gradient(120deg, #2456d9, #4f7dff)',
  },
  { // teal
    solid: '#0e7d94', text: '#0a7286',
    tint: 'rgba(14, 125, 148, 0.12)', soft: 'rgba(14, 125, 148, 0.08)',
    border: 'rgba(14, 125, 148, 0.28)',
    grad: 'linear-gradient(120deg, #0b7f95, #0f93a9)',
  },
  { // purple
    solid: '#a238c9', text: '#9a2fc4',
    tint: 'rgba(162, 56, 201, 0.12)', soft: 'rgba(162, 56, 201, 0.08)',
    border: 'rgba(162, 56, 201, 0.28)',
    grad: 'linear-gradient(120deg, #9a2fc4, #b558e0)',
  },
  { // fuchsia
    solid: '#c9309f', text: '#b3288f',
    tint: 'rgba(201, 48, 159, 0.12)', soft: 'rgba(201, 48, 159, 0.08)',
    border: 'rgba(201, 48, 159, 0.28)',
    grad: 'linear-gradient(120deg, #c72da6, #e547c4)',
  },
] as const;

/** Canonical argomento number from its id ("arg14" → 14); fallback 1. */
export function argomentoNumberFromId(argomentoId: string | undefined | null): number {
  const m = /^arg(\d+)$/i.exec(argomentoId ?? '');
  const n = m ? Number(m[1]) : NaN;
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/** The identity hue for an argomento (stable: number → cycle position). */
export function argomentoHue(argomentoId: string | undefined | null): ArgomentoHue {
  return APPENDICE_HUES[(argomentoNumberFromId(argomentoId) - 1) % APPENDICE_HUES.length];
}
